import AddressField from "kinnect-front/Address/AddressField";
import CarrierField from "kinnect-front/Warehouse/Carriers/CarrierField";
import CustomerField from "kinnect-front/KCustomers/CustomerField";
import SalesmanField from "kinnect-front/KSalesmen/SalesmanField";
import UserField from "kinnect-front/User/UserField";
import {
	Address,
	Carrier,
	Customer,
	Salesman,
	User
} from "kinnect-models";
import {
	Crudable,
	FetchesData
} from "kinnect-mixins";
import {
	KAddressFormBox,
	KCombobox,
	KDatePicker,
	KForm,
	KListTile,
	KTextField,
	KTextarea,
	KTimePicker
} from "kinnect-general";
import { VCol, VRow } from "vuetify/lib";
import { formatDate } from "kinnect-formatters";

export default KForm.extend( {
	name: "OutboundFreightForm",

	props: {
		outboundFreight: Object,

		redirect: {
			...KForm.options.props.redirect,
			default: () => [ "back", { name: "outboundFreightList" } ]
		}
	},

	mixins: [
		Crudable,
		FetchesData
	],

	data: () => ( {
		crud: "outbound_freight",
		carriers: [],
		customers: [],
		salesmen: [],
		users: [],
		addressFormFields: {
			address: null,
			city: null,
			lat: null,
			lng: null,
			state: null,
			zip: null
		},

		formFields: {
			description: null,
			estimatedArrivalDate: null,
			estimatedPickupDate: null,
			estimatedPickupTime: null,
			idAddress: null,
			idCarrier: null,
			idCustomer: null,
			idSalesman: null,
			idUserAssigned: null,
			salesOrders: [],
			trackingNumber: null
		},
		salesOrderNumbers: [],
		today: ( new Date() ).setHours( 0, 0, 0, 0 )
	} ),

	computed: {
		editing() {
			return Boolean( this.outboundFreight );
		},

		todayFormatted() {
			return formatDate( this.today, "yyyy-MM-dd" );
		},

		indexSelectedCustomer() {
			return this.customers.findIndex( customer => this.formFields.idCustomer === customer.id );
		},

		selectedCustomer() {
			if ( this.indexSelectedCustomer !== -1 ) {
				return this.customers[this.indexSelectedCustomer];
			}
		},

		customerAddresses() {
			if ( this.selectedCustomer && this.selectedCustomer.addresses ) {
				return this.selectedCustomer.addresses;
			}

			return [];
		},

		selectedCarrier() {
			return this.carriers.find( carrier => this.formFields.idCarrier === carrier.id );
		},

		isAddressRequired() {
			return Boolean( this.selectedCarrier && this.selectedCarrier.requiresAddress );
		}
	},

	watch: {
		outboundFreight: {
			handler( outboundFreight ) {
				if ( outboundFreight ) {
					Object.keys( this.formFields ).forEach( key => {
						if ( outboundFreight.hasOwnProperty( key )
							&& key !== "address"
							&& key !== "salesOrders"
						) {
							this.formFields[key] = outboundFreight[key];
						}
					} );

					if ( this.outboundFreight.address ) {
						this.addressFormFields.address = this.outboundFreight.address.address;
						this.addressFormFields.city = this.outboundFreight.address.city;
						this.addressFormFields.state = this.outboundFreight.address.state;
						this.addressFormFields.zip = this.outboundFreight.address.zip;
					}

					this.salesOrderNumbers = outboundFreight.salesOrders.map( item => item.salesOrderNumber );
				}
			},
			immediate: true
		},

		"formFields.idCustomer"() {
			this.formFields.idAddress = null;
		},

		salesOrderNumbers: {
			handler( val ) {
				this.formFields.salesOrders = val.map( salesOrderNumber => ( { salesOrderNumber } ) );
			},
			immediate: true
		}
	},

	methods: {
		openDialog( type ) {
			this.dialogs[type] = true;
		},

		submit() {
			if ( !this.formFields.idAddress && this.addressFormFields.address ) {
				return this.createAddress().then( this.submitOutboundFreight );
			}

			return this.submitOutboundFreight();
		},

		createAddress() {
			const newAddress = new Address( this.addressFormFields );

			return newAddress
			.save( false )
			.then( address => this.formFields.idAddress = address.id );
		},

		submitOutboundFreight() {
			const method = this.editing ? "put" : "post";
			const url = this.editing
				? `warehouse/outbound-freight/${this.outboundFreight.id}`
				: "warehouse/outbound-freight";

			return this.$http[method]( url, this.formFields );
		},

		onCreateCarrier( carrier ) {
			this.carriers.push( carrier );
			this.formFields.idCarrier = carrier.id;
		},

		onCreateCustomer( customer ) {
			this.customers.push( customer );
			this.formFields.idCustomer = customer.id;
			this.formFields.idAddress = null;
		},

		onCreateAddress( address ) {
			if ( this.indexSelectedCustomer === -1 ) {
				return;
			}

			this.customers[this.indexSelectedCustomer].addresses.push( address );
			this.formFields.idAddress = address.id;
		},

		onCreateSalesman( salesman ) {
			this.salesmen.push( salesman );
			this.formFields.idSalesman = salesman.id;
		},

		async fetchCarriers() {
			this.carriers = await Carrier.get();
		},

		async fetchCustomers() {
			this.customers = await Customer.get();
		},

		async fetchSalesmen() {
			this.salesmen = await Salesman.get();
		},

		async fetchUsers() {
			this.users = await User.where( "approved", true ).get();
		},

		async fetch() {
			await this.$http.all( [
				this.fetchCarriers(),
				this.fetchCustomers(),
				this.fetchSalesmen(),
				this.fetchUsers()
			] );
		}
	},

	render() {
		return <KForm
			attrs={{
				...this.$props,
				manualSubmitCall: this.submit
			}}
			on={this.$listeners}
		>
			{this.canAdd && <UserField
				items={this.users}
				label="Assigned to*"
				loading={this.$wait.is( "get users" )}
				noDataText="No employees to display"
				required
				vModel={this.formFields.idUserAssigned}
				validationName="assigned to"
				validationRules="required"
			></UserField>}

			<VRow>
				<VCol class="py-0" cols={12} sm={6}>
					<KDatePicker
						datePickerProps={{ min: this.todayFormatted }}
						textFieldProps={{ label: "Estimated Pickup Date" }}
						vModel={this.formFields.estimatedPickupDate}
					></KDatePicker>
				</VCol>

				<VCol class="py-0" cols={12} sm={6}>
					<KTimePicker
						textFieldProps={{ label: "Estimated Pickup Time" }}
						vModel={this.formFields.estimatedPickupTime}
					></KTimePicker>
				</VCol>
			</VRow>

			<KDatePicker
				datePickerProps={{ min: this.todayFormatted }}
				textFieldProps={{
					label: "Estimated Arrival Date",
					prependIcon: "event_available"
				}}
				vModel={this.formFields.estimatedArrivalDate}
			></KDatePicker>

			<CarrierField
				deletableChips
				items={this.carriers}
				label="Carrier"
				loading={this.$wait.is( "get carriers" )}
				onCreate={this.onCreateCarrier}
				showAddButton={this.$can( "add_carriers" )}
				vModel={this.formFields.idCarrier}
			></CarrierField>

			<KTextField
				label="Tracking Number"
				prependIcon="short_text"
				vModel={this.formFields.trackingNumber}
			></KTextField>

			<CustomerField
				deletableChips
				items={this.customers}
				label="Customer"
				loading={this.$wait.is( "get customers" )}
				onCreate={this.onCreateCustomer}
				showAddButton={this.$can( "add_customers" )}
				vModel={this.formFields.idCustomer}
			></CustomerField>

			<transition name="slide-y-transition" mode="out-in">
				{this.selectedCustomer
					? <AddressField
						class="mb-4"
						customer={this.selectedCustomer}
						items={this.customerAddresses}
						label={`Customer Address${this.isAddressRequired ? "*" : ""}`}
						loading={this.$wait.is( "get customers" )}
						onCreate={this.onCreateAddress}
						required={this.isAddressRequired}
						showAddButton={this.$can( "add_addresses" )}
						vModel={this.formFields.idAddress}
						validationName="customer address"
						validationRules={{ required: this.isAddressRequired }}
					></AddressField>
					: <KAddressFormBox
						address={this.addressFormFields.address}
						city={this.addressFormFields.city}
						class="mb-4 ml-blank-icon"
						lat={this.addressFormFields.lat}
						lng={this.addressFormFields.lng}
						onPlacechanged={() => this.formFields.idAddress = null}
						required={this.isAddressRequired}
						state={this.addressFormFields.state}
						zip={this.addressFormFields.zip}
						{...{
							on: {
								"update:address": val => this.addressFormFields.address = val,
								"update:city": val => this.addressFormFields.city = val,
								"update:lat": val => this.addressFormFields.lat = val,
								"update:lng": val => this.addressFormFields.lng = val,
								"update:state": val => this.addressFormFields.state = val,
								"update:zip": val => this.addressFormFields.zip = val
							}
						}}
					></KAddressFormBox>
				}
			</transition>

			<SalesmanField
				items={this.salesmen}
				label="Salesman*"
				loading={this.$wait.is( "get salesmen" )}
				onCreate={this.onCreateSalesman}
				required
				showAddButton={this.$can( "add_salesmen" )}
				vModel={this.formFields.idSalesman}
				validationName="salesman"
				validationRules="required"
			></SalesmanField>

			<KCombobox
				chips
				deletableChips
				delimiters={[ ",", " " ]}
				label="Sales Order Numbers"
				multiple
				prependIcon="format_list_bulleted"
				vModel={this.salesOrderNumbers}
			>
				<KListTile slot="no-data" text="Enter a new sales order number"></KListTile>
			</KCombobox>

			<KTextarea
				label="Description"
				name="description"
				prependIcon="comment"
				vModel={this.formFields.description}
			></KTextarea>
		</KForm>;
	}
} );
